import { Button, Form, Table } from 'react-bootstrap';
import {
    InsurancePolicy,
    InsurancePolicyMainBranch,
    InsurancePolicyPlanTypes,
    PolicyHolder,
    InsuredPersonCoverageSelection,
    SelectedCoverage,
} from '../../../../interfaces/InsurancePolicy';
import { InsuranceAppendix } from '../../../../interfaces/InsuranceAppendix';
import { setValueByField } from '../../../../utils/nestedFieldsLogic';
import { useTranslation } from 'react-i18next';
import { useProductsContext } from '../insuranceProductsContext';
import { useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import { Company } from '../../../../interfaces/Company';
import { getInsuranceDiscounts } from '../../../../utils/api/insuranceDiscounts';
import { InsuranceDiscount, DiscountYear } from '../../../../interfaces/InsuranceDiscount';
import InsuredPersonCoverageSelector from './InsuredPersonCoverageSelector';
import { Trash } from 'react-bootstrap-icons';

interface PolicyInputFieldsProps {
    newInsurancePolicy: InsurancePolicy;
    setNewInsurancePolicy: (newInsurancePolicy: InsurancePolicy) => void;
    companies: Company[];
    additionalActions?: React.ReactNode;
    onCompanyComparisonClick?: (policyWithCoverage: InsurancePolicy) => void;
}

const groupDiscountYears = (years: DiscountYear[]): { range: string; percentage: number }[] => {
    if (!years || years.length === 0) return [];
    const sorted = [...years].sort((a, b) => a.year - b.year);
    const groups: { start: number; end: number; percentage: number }[] = [];

    for (const year of sorted) {
        const lastGroup = groups[groups.length - 1];
        if (lastGroup && year.year === lastGroup.end + 1 && year.percentage === lastGroup.percentage) {
            lastGroup.end = year.year;
        } else {
            groups.push({ start: year.year, end: year.year, percentage: year.percentage });
        }
    }

    return groups.map((g) => ({
        range: g.start === g.end ? g.start.toString() : `${g.start}-${g.end}`,
        percentage: g.percentage,
    }));
};

// Mortgage calculator function
const calculateMortgage = ({ mortgageAmount, period, interestRate }: { mortgageAmount: number; period: number; interestRate: number }) => {
    const monthlyRate = interestRate / 100 / 12;
    let monthlyPayment;

    if (monthlyRate === 0) {
        monthlyPayment = mortgageAmount / period;
    } else {
        monthlyPayment = (mortgageAmount * monthlyRate * Math.pow(1 + monthlyRate, period)) / (Math.pow(1 + monthlyRate, period) - 1);
    }

    let outstandingAmount = mortgageAmount;
    const schedule = [];

    for (let i = 0; i < period; i++) {
        const interestPayment = outstandingAmount * monthlyRate;
        const principalPayment = monthlyPayment - interestPayment;
        outstandingAmount -= principalPayment;

        if (outstandingAmount < 0) {
            outstandingAmount = 0;
        }

        schedule.push({
            month: i + 1,
            outstandingAmount: Number(outstandingAmount.toFixed(2)),
            monthlyPayment: Number(monthlyPayment.toFixed(2)),
        });
    }

    return {
        monthlyPayment: Number(monthlyPayment.toFixed(2)),
        schedule,
    };
};

// Get default total coverage based on branch
const getDefaultTotalCoverage = (branch: InsurancePolicyMainBranch): number => {
    if (branch === InsurancePolicyMainBranch.lifeInsurance) {
        return 1000000;
    }
    if (branch === InsurancePolicyMainBranch.criticalIllness) {
        return 100000;
    }
    return 1; // For health insurance, use 1 (hidden field)
};

// Check if branch requires coverage amount input
const branchRequiresCoverageInput = (branch: InsurancePolicyMainBranch): boolean => {
    return branch === InsurancePolicyMainBranch.lifeInsurance || branch === InsurancePolicyMainBranch.criticalIllness;
};

// Check if branch shows coverage amount (read-only for mortgage insurance)
const branchShowsCoverageAmount = (branch: InsurancePolicyMainBranch): boolean => {
    return (
        branch === InsurancePolicyMainBranch.lifeInsurance ||
        branch === InsurancePolicyMainBranch.criticalIllness ||
        branch === InsurancePolicyMainBranch.mortgageInsurance
    );
};

interface FamilyMemberInfo {
    firstName: string;
    lastName: string;
    idNumber: string;
    dateOfBirth?: Date;
    gender?: string;
}

// Build InsuredPersonCoverageSelection[] from existing appendices, enriched with family member data and policy insuredList
const buildCoverageSelectionsFromAppendices = (
    appendices: InsuranceAppendix[] | undefined,
    familyMembers: FamilyMemberInfo[],
    policyInsuredList?: PolicyHolder[],
): InsuredPersonCoverageSelection[] => {
    if (!appendices || appendices.length === 0) return [];

    // Create a lookup map for family members by idNumber
    const familyMemberMap = new Map<string, FamilyMemberInfo>();
    for (const member of familyMembers) {
        familyMemberMap.set(member.idNumber, member);
    }

    // Create a lookup map for insuredList by idNumber to get smokerStatus
    const insuredListMap = new Map<string, PolicyHolder>();
    if (policyInsuredList) {
        for (const person of policyInsuredList) {
            insuredListMap.set(person.idNumber, person);
        }
    }

    // Group appendices by insured person's idNumber
    const personCoveragesMap = new Map<string, InsuredPersonCoverageSelection>();

    for (const appendix of appendices) {
        if (!appendix.insuredPerson?.idNumber) continue;

        const idNumber = appendix.insuredPerson.idNumber;
        const familyMember = familyMemberMap.get(idNumber);
        const insuredPerson = insuredListMap.get(idNumber);
        // insurancePricing can be a string (ID) or an object with _id
        const pricingId = typeof appendix.insurancePricing === 'string' ? appendix.insurancePricing : (appendix.insurancePricing as any)?._id || '';
        // Extract smoker status from insurancePricing if it's an object
        const smokerStatusFromPricing = typeof appendix.insurancePricing !== 'string' ? (appendix.insurancePricing as any)?.smoker : undefined;
        // Prefer smokerStatus from insuredList, fall back to insurancePricing
        const smokerStatus = (insuredPerson as any)?.smokerStatus || smokerStatusFromPricing;

        const coverage: SelectedCoverage = {
            coverageType: appendix.domain || appendix.branch,
            insurancePricingId: pricingId,
        };

        if (personCoveragesMap.has(idNumber)) {
            const existing = personCoveragesMap.get(idNumber)!;
            // Only add if this coverage type doesn't already exist for this person
            const alreadyHasCoverageType = existing.selectedCoverages.some(
                (sc) => sc.coverageType === coverage.coverageType
            );
            if (!alreadyHasCoverageType) {
                existing.selectedCoverages.push(coverage);
            }
            // Update smoker status if we found one and it's not already set
            if (smokerStatus && !existing.smokerStatus) {
                existing.smokerStatus = smokerStatus;
            }
        } else {
            personCoveragesMap.set(idNumber, {
                insuredPerson: {
                    name: appendix.insuredPerson.name,
                    idNumber: appendix.insuredPerson.idNumber,
                    dateOfBirth: familyMember?.dateOfBirth,
                    gender: familyMember?.gender as any,
                },
                selectedCoverages: [coverage],
                smokerStatus: smokerStatus,
            });
        }
    }

    return Array.from(personCoveragesMap.values());
};

// Build PolicyHolder[] (insuredList) from existing appendices, enriched with family member data
// Also preserves smokerStatus from existing insuredList if available
const buildInsuredListFromAppendices = (
    appendices: InsuranceAppendix[] | undefined,
    familyMembers: FamilyMemberInfo[],
    existingInsuredList?: PolicyHolder[],
): PolicyHolder[] => {
    if (!appendices || appendices.length === 0) return [];

    // Create a lookup map for family members by idNumber
    const familyMemberMap = new Map<string, FamilyMemberInfo>();
    for (const member of familyMembers) {
        familyMemberMap.set(member.idNumber, member);
    }

    // Create a lookup map for existing insuredList by idNumber
    const existingInsuredMap = new Map<string, PolicyHolder>();
    if (existingInsuredList) {
        for (const person of existingInsuredList) {
            existingInsuredMap.set(person.idNumber, person);
        }
    }

    // Use a Map to deduplicate by idNumber
    const personsMap = new Map<string, PolicyHolder>();

    for (const appendix of appendices) {
        if (!appendix.insuredPerson?.idNumber) continue;

        const idNumber = appendix.insuredPerson.idNumber;
        if (!personsMap.has(idNumber)) {
            const familyMember = familyMemberMap.get(idNumber);
            const existingPerson = existingInsuredMap.get(idNumber);
            personsMap.set(idNumber, {
                name: appendix.insuredPerson.name,
                idNumber: appendix.insuredPerson.idNumber,
                dateOfBirth: familyMember?.dateOfBirth,
                gender: familyMember?.gender as any,
                smokerStatus: (existingPerson as any)?.smokerStatus,
            } as PolicyHolder);
        }
    }

    return Array.from(personsMap.values());
};

const PolicyInputFields = ({
    newInsurancePolicy,
    setNewInsurancePolicy,
    companies,
    additionalActions,
    onCompanyComparisonClick,
}: PolicyInputFieldsProps) => {
    const { t } = useTranslation('insurance');
    const { consts } = useProductsContext();
    const familyMembers = consts.familyMembers;
    const [subBranches, setSubBranches] = useState<string[]>([]);
    const [discounts, setDiscounts] = useState<InsuranceDiscount[]>([]);
    const [isLoadingDiscounts, setIsLoadingDiscounts] = useState(false);
    const [totalCoverage, setTotalCoverage] = useState<number>(
        newInsurancePolicy.totalCoverage || getDefaultTotalCoverage(newInsurancePolicy.branch),
    );

    // Sync default totalCoverage to parent on mount if not already set
    useEffect(() => {
        if (!newInsurancePolicy.totalCoverage && totalCoverage > 0) {
            setNewInsurancePolicy({
                ...newInsurancePolicy,
                totalCoverage: totalCoverage,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only on mount

    const isReplacementPolicy = newInsurancePolicy.replacementPolicies && newInsurancePolicy.replacementPolicies.length > 0;

    // Track previous company and branch to detect actual changes
    const prevCompanyRef = useRef(newInsurancePolicy.company);
    const prevBranchRef = useRef(newInsurancePolicy.branch);
    const initializedFromAppendicesRef = useRef(false);

    // Create a stable value for insuredList to use in useEffect dependencies
    const insuredListKey = (newInsurancePolicy.insuredList || [])
        .map((p) => `${p.idNumber}-${p.dateOfBirth}`)
        .sort()
        .join('|');

    // Rebuild coverage selections from appendices on mount (only once), using familyMembers for dateOfBirth/gender
    useEffect(() => {
        if (initializedFromAppendicesRef.current) return;
        initializedFromAppendicesRef.current = true;

        // If no insuredPersonCoverages but appendices exist, rebuild from appendices with family member data
        if (
            (!newInsurancePolicy.insuredPersonCoverages || newInsurancePolicy.insuredPersonCoverages.length === 0) &&
            newInsurancePolicy.appendices &&
            newInsurancePolicy.appendices.length > 0
        ) {
            const rebuiltCoverages = buildCoverageSelectionsFromAppendices(
                newInsurancePolicy.appendices,
                familyMembers,
                newInsurancePolicy.insuredList,
            );
            const insuredListFromAppendices = buildInsuredListFromAppendices(
                newInsurancePolicy.appendices,
                familyMembers,
                newInsurancePolicy.insuredList,
            );

            setNewInsurancePolicy({
                ...newInsurancePolicy,
                insuredList: insuredListFromAppendices.length > 0 ? insuredListFromAppendices : newInsurancePolicy.insuredList,
                insuredPersonCoverages: rebuiltCoverages,
                totalCoverage: totalCoverage, // Ensure totalCoverage is included
            });
        }
    }, [familyMembers]);

    const handlePolicyInputChange = (e: any) => {
        const name = e.target.name;
        const value = name.toLowerCase().includes('date') ? new Date(e.target.value) : e.target.value;
        const updatedPolicy = setValueByField({ ...newInsurancePolicy }, name, value);
        setNewInsurancePolicy(updatedPolicy);
    };

    useEffect(() => {
        if (!newInsurancePolicy.company) {
            setNewInsurancePolicy({
                ...newInsurancePolicy,
                clientId: typeof newInsurancePolicy.client === 'string' ? newInsurancePolicy.client : (newInsurancePolicy.client as any)?._id,
                company: companies[0]._id,
            });
        }
    }, [companies, newInsurancePolicy.company, setNewInsurancePolicy]);

    useEffect(() => {
        const subBranches = newInsurancePolicy.appendices?.map((appendix) => appendix.branch);
        setSubBranches(Array.from(new Set(subBranches ?? [])));
    }, [newInsurancePolicy.appendices]);

    const handlePolicyChange = (updatedPolicy: InsurancePolicy) => {
        setNewInsurancePolicy({
            ...updatedPolicy,
            totalCoverage: totalCoverage, // Ensure totalCoverage is always included from local state
        });
    };

    const handleTotalCoverageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value) || 0;
        setTotalCoverage(value);
        setNewInsurancePolicy({
            ...newInsurancePolicy,
            totalCoverage: value,
        });
    };

    // Update totalCoverage, reset selected coverages, discount and insured persons when branch changes
    useEffect(() => {
        const currentBranch = newInsurancePolicy.branch;
        if (prevBranchRef.current !== currentBranch) {
            prevBranchRef.current = currentBranch;
            const defaultCoverage = getDefaultTotalCoverage(currentBranch);
            setTotalCoverage(defaultCoverage);

            // Initialize mortgage details with one empty line for mortgage insurance
            const initialMortgageDetails =
                currentBranch === InsurancePolicyMainBranch.mortgageInsurance
                    ? [{ amount: 0, period: 0, interestRate: 0 }]
                    : newInsurancePolicy.mortgageDetails;

            setNewInsurancePolicy({
                ...newInsurancePolicy,
                totalCoverage: defaultCoverage,
                insuredPersonCoverages: [],
                insuredList: [],
                insuranceDiscount: undefined,
                mortgageDetails: initialMortgageDetails,
            });
        }
    }, [newInsurancePolicy.branch]);

    // Calculate total coverage from mortgage details for mortgage insurance
    useEffect(() => {
        if (newInsurancePolicy.branch === InsurancePolicyMainBranch.mortgageInsurance) {
            const mortgageDetails = newInsurancePolicy.mortgageDetails || [];
            const calculatedCoverage = mortgageDetails.reduce((sum, detail) => sum + (detail.amount || 0), 0);
            if (calculatedCoverage !== totalCoverage) {
                setTotalCoverage(calculatedCoverage);
                setNewInsurancePolicy({
                    ...newInsurancePolicy,
                    totalCoverage: calculatedCoverage,
                });
            }
        }
    }, [newInsurancePolicy.branch, newInsurancePolicy.mortgageDetails]);

    // Reset discount and coverages when company changes
    useEffect(() => {
        const currentCompany = typeof newInsurancePolicy.company === 'string' ? newInsurancePolicy.company : newInsurancePolicy.company?._id;
        const prevCompany = typeof prevCompanyRef.current === 'string' ? prevCompanyRef.current : (prevCompanyRef.current as any)?._id;
        if (prevCompany && currentCompany && prevCompany !== currentCompany) {
            prevCompanyRef.current = newInsurancePolicy.company;
            setNewInsurancePolicy({
                ...newInsurancePolicy,
                insuranceDiscount: undefined,
                insuredPersonCoverages: [],
            });
        } else {
            prevCompanyRef.current = newInsurancePolicy.company;
        }
    }, [newInsurancePolicy.company]);

    useEffect(() => {
        const fetchDiscounts = async () => {
            if (!newInsurancePolicy.company || !newInsurancePolicy.branch) {
                setDiscounts([]);
                return;
            }

            setIsLoadingDiscounts(true);
            try {
                const companyId = typeof newInsurancePolicy.company === 'string' ? newInsurancePolicy.company : newInsurancePolicy.company._id;
                const params: any = {
                    company: companyId,
                    branch: newInsurancePolicy.branch,
                    limit: 100,
                };

                // Add coverage parameter if available
                if (totalCoverage && totalCoverage > 0) {
                    params.coverage = totalCoverage;
                }

                // Add age parameter - use oldest insured person's age
                const insuredList = newInsurancePolicy.insuredList || [];
                if (insuredList.length > 0) {
                    const ages = insuredList
                        .map((person) => {
                            if (!person.dateOfBirth) return null;
                            const birthDate = new Date(person.dateOfBirth);
                            const today = new Date();
                            let age = today.getFullYear() - birthDate.getFullYear();
                            const monthDiff = today.getMonth() - birthDate.getMonth();
                            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                                age--;
                            }
                            return age;
                        })
                        .filter((age) => age !== null) as number[];

                    if (ages.length > 0) {
                        // Use oldest age to get discounts (most restrictive for age limits)
                        params.age = Math.max(...ages);
                    }
                }

                const response = await getInsuranceDiscounts(params);
                setDiscounts(response.discounts || []);
            } catch (error) {
                console.error('Failed to fetch discounts', error);
            } finally {
                setIsLoadingDiscounts(false);
            }
        };
        fetchDiscounts();
    }, [newInsurancePolicy.company, newInsurancePolicy.branch, totalCoverage, insuredListKey]);

    return (
        <>
            {/* Row 1: Insurance Type + Coverage Amount (if relevant) + Start Date */}
            <div className="row mb-3">
                <Form.Group
                    className={branchShowsCoverageAmount(newInsurancePolicy.branch) ? 'col-md-4' : 'col-md-6'}
                    controlId="InsurancePolicyMainBranch"
                >
                    <Form.Label>{t('Insurance Type')}</Form.Label>
                    <Select
                        options={[
                            { value: InsurancePolicyMainBranch.healthInsurance, label: t('Health Insurance') },
                            { value: InsurancePolicyMainBranch.lifeInsurance, label: t('Life Insurance') },
                            { value: InsurancePolicyMainBranch.criticalIllness, label: t('Critical Illness') },
                            { value: InsurancePolicyMainBranch.mortgageInsurance, label: t('Mortgage Insurance') },
                        ]}
                        value={
                            newInsurancePolicy.branch
                                ? {
                                      value: newInsurancePolicy.branch,
                                      label:
                                          newInsurancePolicy.branch === InsurancePolicyMainBranch.healthInsurance
                                              ? t('Health Insurance')
                                              : newInsurancePolicy.branch === InsurancePolicyMainBranch.lifeInsurance
                                                ? t('Life Insurance')
                                                : newInsurancePolicy.branch === InsurancePolicyMainBranch.criticalIllness
                                                  ? t('Critical Illness')
                                                  : t('Mortgage Insurance'),
                                  }
                                : null
                        }
                        onChange={(option) => {
                            if (option) {
                                setNewInsurancePolicy({
                                    ...newInsurancePolicy,
                                    branch: option.value,
                                });
                            }
                        }}
                        isDisabled={isReplacementPolicy}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </Form.Group>

                {/* Coverage Amount - shown for life insurance, critical illness, and mortgage insurance */}
                {branchShowsCoverageAmount(newInsurancePolicy.branch) && (
                    <Form.Group className="col-md-4" controlId="totalCoverage">
                        <Form.Label>{t('Coverage Amount')}</Form.Label>
                        <Form.Control
                            type="number"
                            name="totalCoverage"
                            min={0}                            
                            value={totalCoverage}
                            onChange={handleTotalCoverageChange}
                            readOnly={newInsurancePolicy.branch === InsurancePolicyMainBranch.mortgageInsurance}
                            disabled={newInsurancePolicy.branch === InsurancePolicyMainBranch.mortgageInsurance}
                        />
                        {newInsurancePolicy.branch === InsurancePolicyMainBranch.mortgageInsurance && (
                            <Form.Text className="text-muted">{t('Calculated from mortgage details')}</Form.Text>
                        )}
                    </Form.Group>
                )}

                <Form.Group className={branchShowsCoverageAmount(newInsurancePolicy.branch) ? 'col-md-4' : 'col-md-6'} controlId="insuranceStartDate">
                    <Form.Label>{t('Start Date')}</Form.Label>
                    <Form.Control
                        type="date"
                        name="insuranceStartDate"
                        value={
                            newInsurancePolicy.insuranceStartDate
                                ? new Date(newInsurancePolicy.insuranceStartDate).toISOString().split('T')[0]
                                : new Date(new Date().setMonth(new Date().getMonth() + 1, 1)).toISOString().split('T')[0]
                        }
                        onChange={handlePolicyInputChange}
                    />
                </Form.Group>
            </div>

            {/* Row 2: Insurance Company + Discount + Company Comparison Button */}
            <div className="row mb-3">
                <Form.Group className="col-md-4" controlId="companyName">
                    <Form.Label>{t('Insurance Company')}</Form.Label>
                    <Select
                        options={companies.map((company) => ({
                            value: company._id,
                            label: company.shortName ?? company.name,
                        }))}
                        value={
                            companies
                                .filter((c) => {
                                    const companyId =
                                        typeof newInsurancePolicy.company === 'string' ? newInsurancePolicy.company : newInsurancePolicy.company?._id;
                                    return c._id === companyId;
                                })
                                .map((c) => ({ value: c._id, label: c.shortName ?? c.name }))[0] || null
                        }
                        onChange={(option) => {
                            if (option) {
                                setNewInsurancePolicy({
                                    ...newInsurancePolicy,
                                    company: option.value,
                                });
                            }
                        }}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </Form.Group>

                <Form.Group className="col-md-4" controlId="discountSelect">
                    <Form.Label>{t('Discount')}</Form.Label>
                    <Select
                        options={discounts.map((d) => {
                            const yearsStr = d.discountYears
                                ? groupDiscountYears(d.discountYears)
                                      .map((g) => `${g.range}: ${g.percentage}%`)
                                      .join(', ')
                                : '';
                            return {
                                value: d._id,
                                label: `${d.description} (${yearsStr})`,
                            };
                        })}
                        value={
                            newInsurancePolicy.insuranceDiscount
                                ? (() => {
                                      const d = discounts.find((disc) => disc._id === newInsurancePolicy.insuranceDiscount);
                                      if (!d) return null;
                                      const yearsStr = d.discountYears
                                          ? groupDiscountYears(d.discountYears)
                                                .map((g) => `${g.range}: ${g.percentage}%`)
                                                .join(', ')
                                          : '';
                                      return {
                                          value: d._id,
                                          label: `${d.description} (${yearsStr})`,
                                      };
                                  })()
                                : null
                        }
                        onChange={(option) => {
                            setNewInsurancePolicy({
                                ...newInsurancePolicy,
                                insuranceDiscount: option?.value || undefined,
                            });
                        }}
                        isLoading={isLoadingDiscounts}
                        isClearable
                        placeholder={t('Select Discount...')}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                </Form.Group>

                {onCompanyComparisonClick && (
                    <Form.Group className="col-md-4 d-flex align-items-end" controlId="companyComparison">
                        <Button
                            variant="outline-primary"
                            onClick={(e) => {
                                e.preventDefault();
                                // Pass policy with current totalCoverage to ensure it's synced
                                onCompanyComparisonClick({
                                    ...newInsurancePolicy,
                                    totalCoverage: totalCoverage,
                                });
                            }}
                            style={{ height: '38px' }}
                        >
                            <i className="mdi mdi-compare me-1"></i>
                            {t('Company Comparison')}
                        </Button>
                    </Form.Group>
                )}
            </div>

            {/* Mortgage Details Table - only shown for mortgage insurance */}
            {newInsurancePolicy.branch === InsurancePolicyMainBranch.mortgageInsurance && (
                <div className="mb-4">
                    <Form.Label>{t('Mortgage Details')}</Form.Label>
                    <Table bordered hover>
                        <thead>
                            <tr>
                                <th>{t('Amount')}</th>
                                <th>{t('Period (Months)')}</th>
                                <th>{t('Interest Rate')} %</th>
                                <th>{t('Actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(newInsurancePolicy.mortgageDetails || []).map((detail: any, index: number) => (
                                <tr key={index}>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            value={detail.amount || ''}
                                            min="0.01"
                                            step="0.01"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    // Find next input in row or first in next row
                                                    const form = e.currentTarget.form;
                                                    if (form) {
                                                        const index = Array.prototype.indexOf.call(form, e.currentTarget);
                                                        (form.elements[index + 1] as HTMLElement).focus();
                                                    }
                                                }
                                            }}
                                            onChange={(e) => {
                                                let val = Number(e.target.value);
                                                if (val < 0) {
                                                    val = 0;
                                                }
                                                const newDetails = [...(newInsurancePolicy.mortgageDetails || [])];
                                                newDetails[index] = { ...newDetails[index], amount: val };
                                                setNewInsurancePolicy({ ...newInsurancePolicy, mortgageDetails: newDetails });
                                            }}
                                            placeholder={t('Amount')}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            value={detail.period || ''}
                                            min="1"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    const form = e.currentTarget.form;
                                                    if (form) {
                                                        const index = Array.prototype.indexOf.call(form, e.currentTarget);
                                                        (form.elements[index + 1] as HTMLElement).focus();
                                                    }
                                                }
                                            }}
                                            onChange={(e) => {
                                                let val = Number(e.target.value);
                                                if (val < 0) {
                                                    val = 0;
                                                }
                                                const newDetails = [...(newInsurancePolicy.mortgageDetails || [])];
                                                newDetails[index] = { ...newDetails[index], period: val };
                                                setNewInsurancePolicy({ ...newInsurancePolicy, mortgageDetails: newDetails });
                                            }}
                                            placeholder={t('Period')}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            max="10"
                                            value={detail.interestRate || ''}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    // If last input in row, try to add new row or focus next
                                                    const form = e.currentTarget.form;
                                                    if (form) {
                                                        const index = Array.prototype.indexOf.call(form, e.currentTarget);
                                                        (form.elements[index + 1] as HTMLElement).focus();
                                                    }
                                                }
                                            }}
                                            onChange={(e) => {
                                                let val = Number(e.target.value);
                                                if (val < 0) {
                                                    val = 0;
                                                } else if (val > 10) {
                                                    val = 10;
                                                }
                                                const newDetails = [...(newInsurancePolicy.mortgageDetails || [])];
                                                newDetails[index] = { ...newDetails[index], interestRate: val };
                                                setNewInsurancePolicy({ ...newInsurancePolicy, mortgageDetails: newDetails });
                                            }}
                                            placeholder={t('Interest Rate')}
                                        />
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => {
                                                const newDetails = [...(newInsurancePolicy.mortgageDetails || [])];
                                                newDetails.splice(index, 1);
                                                setNewInsurancePolicy({ ...newInsurancePolicy, mortgageDetails: newDetails });
                                            }}
                                        >
                                            <Trash />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => {
                            const newDetails = [...(newInsurancePolicy.mortgageDetails || []), { amount: 0, period: 0, interestRate: 0 }];
                            setNewInsurancePolicy({ ...newInsurancePolicy, mortgageDetails: newDetails });
                        }}
                    >
                        {t('Add Mortgage Line')}
                    </Button>
                </div>
            )}

            {/* Combined Insured Persons and Coverages */}
            <Form.Group className="mb-3" controlId="insuredPersonsAndCoverages">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="mb-0">{t('Insured Persons')}</Form.Label>
                    {additionalActions && <div>{additionalActions}</div>}
                </div>
                <InsuredPersonCoverageSelector
                    policy={newInsurancePolicy}
                    familyMembers={familyMembers}
                    onPolicyChange={handlePolicyChange}
                    insuranceDiscount={
                        newInsurancePolicy.insuranceDiscount ? discounts.find((d) => d._id === newInsurancePolicy.insuranceDiscount) : undefined
                    }
                />
            </Form.Group>

            {/* Remarks */}
            <Form.Group className="mb-3" controlId="remarks">
                <Form.Label>{t('Remarks')}</Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    name="remarks"
                    value={newInsurancePolicy.remarks || ''}
                    onChange={handlePolicyInputChange}
                    placeholder={t('Add notes or remarks about the policy...')}
                />
            </Form.Group>
        </>
    );
};

export default PolicyInputFields;
